import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Bell, Trash2, CheckCheck } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotificationStore } from '../../store/notificationStore';
import { getNotificationConfig } from '../../types/notification.types';
import type { AppNotification } from '../../types/notification.types';

// ─── Date group helpers ───────────────────────────────────────────────────────

function dateLabel(iso: string): string {
  const d = new Date(iso);
  if (isToday(d)) return 'Hoy';
  if (isYesterday(d)) return 'Ayer';
  return format(d, 'd MMM yyyy', { locale: es });
}

function groupByDate(
  items: AppNotification[]
): { label: string; items: AppNotification[] }[] {
  const map = new Map<string, AppNotification[]>();
  for (const n of items) {
    const label = dateLabel(n.createdAt);
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(n);
  }
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
}

// ─── Single notification item ─────────────────────────────────────────────────

function NotificationItem({
  n,
  onDismiss,
}: {
  n: AppNotification;
  onDismiss: (id: string) => void;
}) {
  const navigate = useNavigate();
  const { markRead } = useNotificationStore();
  const cfg = getNotificationConfig(n.type);
  const actionUrl = n.data?.actionUrl as string | undefined;

  const handleClick = () => {
    markRead(n.notificationId);
    if (actionUrl) navigate(actionUrl);
  };

  return (
    <div
      className={`relative flex items-start gap-3 px-4 py-3 transition-colors ${
        n.read ? 'bg-white' : 'bg-blue-50'
      } ${actionUrl ? 'cursor-pointer hover:bg-gray-50' : ''}`}
      onClick={handleClick}
    >
      {/* Icon */}
      <div
        className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-base ${cfg.bg}`}
      >
        {cfg.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${cfg.color} leading-snug`}>
          {n.title}
        </p>
        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
          {n.message}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {format(new Date(n.createdAt), 'HH:mm', { locale: es })}
        </p>
      </div>

      {/* Unread dot */}
      {!n.read && (
        <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />
      )}

      {/* Dismiss */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(n.notificationId);
        }}
        className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Eliminar"
      >
        <X size={12} />
      </button>
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export function NotificationPanel() {
  const {
    notifications,
    unreadCount,
    isPanelOpen,
    closePanel,
    markAllRead,
    clearAll,
    removeNotification,
  } = useNotificationStore();

  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isPanelOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isPanelOpen, closePanel]);

  // Close on Escape
  useEffect(() => {
    if (!isPanelOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isPanelOpen, closePanel]);

  if (!isPanelOpen) return null;

  const groups = groupByDate(notifications);

  return (
    <>
      {/* Backdrop (mobile) */}
      <div
        className="fixed inset-0 bg-black/20 z-40 lg:hidden"
        onClick={closePanel}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col lg:top-16 lg:h-[calc(100vh-4rem)] border-l border-gray-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-gray-700" />
            <span className="font-semibold text-gray-900">Notificaciones</span>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {notifications.length > 0 && (
              <>
                <button
                  onClick={markAllRead}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                  title="Marcar todas como leídas"
                >
                  <CheckCheck size={16} />
                </button>
                <button
                  onClick={clearAll}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors"
                  title="Limpiar todas"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
            <button
              onClick={closePanel}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 p-8">
              <Bell size={36} className="opacity-30" />
              <p className="text-sm text-center">
                No hay notificaciones aún.
                <br />
                Las recibirás en tiempo real.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {groups.map((group) => (
                <div key={group.label}>
                  <div className="px-4 py-2 bg-gray-50 sticky top-0">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {group.label}
                    </span>
                  </div>
                  <div className="group">
                    {group.items.map((n) => (
                      <NotificationItem
                        key={n.notificationId}
                        n={n}
                        onDismiss={removeNotification}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
